/**
 * Product Hero Gallery custom Lightbox gestures.
 *
 * Owns zoom, pan, swipe, pinch, pointer capture, and gesture cleanup. This is
 * separate from the custom Lightbox core and third-party gallery runtimes.
 */

(() => {
  const MODULE_KEY = '__MarineMaxProductHeroGalleryLightboxGestures';

  if (window[MODULE_KEY]?.install) return;

  class ProductHeroGalleryLightboxGesturesBehavior {
    initializeLightboxGesturesState() {
      this.lightboxSwipeClickTimer = null;
      this.resetLightboxInteractionState = null;
    }

    clearLightboxGestureTimer() {
      if (!this.lightboxSwipeClickTimer) return;

      window.clearTimeout(this.lightboxSwipeClickTimer);
      this.lightboxSwipeClickTimer = null;
    }

    destroyLightboxGestures() {
      this.clearLightboxGestureTimer();
      this.resetLightboxInteractionState = null;
    }

    initLightboxInteractions() {
      if (!this.lightboxStage || !this.lightboxImage) return;

      this.lightboxImage.addEventListener('dblclick', (event) => {
        event.preventDefault();
        if (this.lightboxZoom > 1) {
          this.setLightboxZoom(1);
        } else {
          this.setLightboxZoom(2);
        }
      }, { signal: this.eventAbortController.signal });

      this.lightboxStage.addEventListener(
        'wheel',
        (event) => {
          if (!this.lightbox.classList.contains('is-open')) return;
          event.preventDefault();
          const direction = event.deltaY < 0 ? 0.25 : -0.25;
          this.zoomLightboxBy(direction);
        },
        { passive: false, signal: this.eventAbortController.signal },
      );

      const swipeThreshold = 36;
      const axisLockThreshold = 10;
      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let deltaY = 0;
      let axisLocked = null;
      let activePointerId = null;
      let trackingTouch = false;
      let suppressNextClick = false;
      const activeTouchPoints = new Map();
      let pinchState = null;

      const getTouchPair = () => Array.from(activeTouchPoints.values()).slice(0, 2);
      const getTouchDistance = (firstPoint, secondPoint) =>
        Math.hypot(secondPoint.clientX - firstPoint.clientX, secondPoint.clientY - firstPoint.clientY);
      const getTouchMidpoint = (firstPoint, secondPoint) => ({
        x: (firstPoint.clientX + secondPoint.clientX) / 2,
        y: (firstPoint.clientY + secondPoint.clientY) / 2,
      });

      const startPinch = () => {
        const [firstPoint, secondPoint] = getTouchPair();
        if (!firstPoint || !secondPoint) return;

        const startDistance = getTouchDistance(firstPoint, secondPoint);
        if (!startDistance) return;

        const midpoint = getTouchMidpoint(firstPoint, secondPoint);
        const stageRect = this.lightboxStage.getBoundingClientRect();
        const stageCenterX = stageRect.width / 2;
        const stageCenterY = stageRect.height / 2;
        const midpointX = midpoint.x - stageRect.left;
        const midpointY = midpoint.y - stageRect.top;
        const currentZoom = Math.max(this.lightboxZoom, 1);

        pinchState = {
          startDistance,
          startZoom: currentZoom,
          contentX: (midpointX - stageCenterX - this.lightboxPanX) / currentZoom,
          contentY: (midpointY - stageCenterY - this.lightboxPanY) / currentZoom,
        };

        this.setLightboxGestureState(true);
        trackingTouch = false;
        activePointerId = null;
        axisLocked = null;
        deltaX = 0;
        deltaY = 0;
        this.lightboxPointerState = null;
      };

      const updatePinch = () => {
        if (!pinchState) return false;

        const [firstPoint, secondPoint] = getTouchPair();
        if (!firstPoint || !secondPoint) return false;

        const nextDistance = getTouchDistance(firstPoint, secondPoint);
        if (!nextDistance) return false;

        const midpoint = getTouchMidpoint(firstPoint, secondPoint);
        const stageRect = this.lightboxStage.getBoundingClientRect();
        const stageCenterX = stageRect.width / 2;
        const stageCenterY = stageRect.height / 2;
        const midpointX = midpoint.x - stageRect.left;
        const midpointY = midpoint.y - stageRect.top;
        const nextZoom = Math.min(Math.max(pinchState.startZoom * (nextDistance / pinchState.startDistance), 1), 3);
        const nextPanX = midpointX - stageCenterX - pinchState.contentX * nextZoom;
        const nextPanY = midpointY - stageCenterY - pinchState.contentY * nextZoom;

        this.updateLightboxTransform({
          zoom: nextZoom,
          panX: nextZoom === 1 ? 0 : nextPanX,
          panY: nextZoom === 1 ? 0 : nextPanY,
        });

        return true;
      };

      const capturePointer = (pointerId) => {
        try {
          this.lightboxStage.setPointerCapture?.(pointerId);
        } catch (_) {}
      };

      const resetPointer = () => {
        activeTouchPoints.forEach((_, pointerId) => releasePointer(pointerId));
        if (activePointerId !== null) releasePointer(activePointerId);
        activeTouchPoints.clear();
        this.lightboxPointerState = null;
        activePointerId = null;
        trackingTouch = false;
        startX = 0;
        startY = 0;
        deltaX = 0;
        deltaY = 0;
        axisLocked = null;
        pinchState = null;
        this.setLightboxGestureState(false);
      };
      this.resetLightboxInteractionState = resetPointer;

      const releasePointer = (pointerId) => {
        try {
          if (!this.lightboxStage.hasPointerCapture || this.lightboxStage.hasPointerCapture(pointerId)) {
            this.lightboxStage.releasePointerCapture?.(pointerId);
          }
        } catch (_) {}
      };

      const finishSwipe = () => {
        if (!trackingTouch) {
          resetPointer();
          return;
        }

        if (
          this.lightboxZoom <= 1 &&
          axisLocked === 'x' &&
          Math.abs(deltaX) >= swipeThreshold &&
          Math.abs(deltaX) > Math.abs(deltaY)
        ) {
          suppressNextClick = true;
          if (deltaX < 0) {
            this.lightboxNext();
          } else {
            this.lightboxPrev();
          }

          window.clearTimeout(this.lightboxSwipeClickTimer);
          this.lightboxSwipeClickTimer = window.setTimeout(() => {
            suppressNextClick = false;
            this.lightboxSwipeClickTimer = null;
          }, 250);
        }

        resetPointer();
      };

      const onPointerMove = (clientX, clientY) => {
        if (!trackingTouch) return false;

        deltaX = clientX - startX;
        deltaY = clientY - startY;

        if (this.lightboxZoom > 1 && this.lightboxPointerState) {
          const nextPanX = this.lightboxPointerState.panX + deltaX;
          const nextPanY = this.lightboxPointerState.panY + deltaY;
          this.updateLightboxTransform({ panX: nextPanX, panY: nextPanY });
          return true;
        }

        if (!axisLocked) {
          const absX = Math.abs(deltaX);
          const absY = Math.abs(deltaY);

          if (absX < axisLockThreshold && absY < axisLockThreshold) {
            return false;
          }

          axisLocked = absX > absY ? 'x' : 'y';
        }

        return axisLocked === 'x';
      };

      this.lightboxStage.addEventListener(
        'pointerdown',
        (event) => {
          if (event.pointerType !== 'touch' && event.pointerType !== 'mouse') return;
          if (event.pointerType === 'touch') {
            capturePointer(event.pointerId);
            activeTouchPoints.set(event.pointerId, {
              clientX: event.clientX,
              clientY: event.clientY,
            });
            if (activeTouchPoints.size >= 2) {
              startPinch();
              return;
            }
          }
          if (event.pointerType === 'mouse' && this.lightboxZoom <= 1) return;
          if (event.pointerType === 'mouse') capturePointer(event.pointerId);
          this.setLightboxGestureState(true);
          trackingTouch = true;
          activePointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          deltaX = 0;
          deltaY = 0;
          axisLocked = null;
          this.lightboxPointerState = {
            panX: this.lightboxPanX,
            panY: this.lightboxPanY,
          };
        },
        { passive: true, signal: this.eventAbortController.signal },
      );

      this.lightboxStage.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType === 'touch' && activeTouchPoints.has(event.pointerId)) {
            activeTouchPoints.set(event.pointerId, {
              clientX: event.clientX,
              clientY: event.clientY,
            });

            if (pinchState && activeTouchPoints.size >= 2) {
              const consumed = updatePinch();
              if (consumed) {
                event.preventDefault();
              }
              return;
            }
          }

          if (event.pointerId !== activePointerId) return;
          const consumed = onPointerMove(event.clientX, event.clientY);
          if (consumed) {
            event.preventDefault();
          }
        },
        { passive: false, signal: this.eventAbortController.signal },
      );

      this.lightboxStage.addEventListener('pointerup', (event) => {
        releasePointer(event.pointerId);
        if (event.pointerType === 'touch') {
          activeTouchPoints.delete(event.pointerId);

          if (pinchState && activeTouchPoints.size < 2) {
            pinchState = null;

            if (activeTouchPoints.size === 1 && this.lightboxZoom > 1) {
              const [remainingPointerId, remainingPoint] = activeTouchPoints.entries().next().value || [];
              if (remainingPoint) {
                trackingTouch = true;
                activePointerId = remainingPointerId;
                startX = remainingPoint.clientX;
                startY = remainingPoint.clientY;
                deltaX = 0;
                deltaY = 0;
                axisLocked = null;
                this.lightboxPointerState = {
                  panX: this.lightboxPanX,
                  panY: this.lightboxPanY,
                };
              }
              return;
            }

            resetPointer();
            return;
          }
        }

        if (event.pointerId !== activePointerId) return;
        finishSwipe();
      }, { signal: this.eventAbortController.signal });

      this.lightboxStage.addEventListener('pointercancel', (event) => {
        releasePointer(event.pointerId);
        if (event.pointerType === 'touch') {
          activeTouchPoints.delete(event.pointerId);

          if (pinchState && activeTouchPoints.size < 2) {
            resetPointer();
            return;
          }
        }
        if (event.pointerId !== activePointerId) return;
        resetPointer();
      }, { signal: this.eventAbortController.signal });

      this.lightboxStage.addEventListener(
        'click',
        (event) => {
          if (!suppressNextClick) return;
          event.preventDefault();
          event.stopPropagation();
        },
        { capture: true, signal: this.eventAbortController.signal },
      );
    }


    resetLightboxZoom() {
      this.setLightboxZoom(1);
    }

    setLightboxGestureState(isActive) {
      if (!this.lightbox) return;
      this.lightbox.classList.toggle('is-gesturing', isActive);
      if (this.lightboxImage && this.lightboxZoom > 1) {
        this.lightboxImage.style.cursor = isActive ? 'grabbing' : 'grab';
      }
    }

    zoomLightboxBy(delta) {
      this.setLightboxZoom(this.lightboxZoom + delta);
    }

    setLightboxZoom(nextZoom) {
      const normalizedZoom = Math.min(Math.max(nextZoom, 1), 3);
      this.updateLightboxTransform({
        zoom: normalizedZoom,
        panX: normalizedZoom === 1 ? 0 : this.lightboxPanX,
        panY: normalizedZoom === 1 ? 0 : this.lightboxPanY,
      });
    }

    updateLightboxTransform({ zoom = this.lightboxZoom, panX = this.lightboxPanX, panY = this.lightboxPanY } = {}) {
      if (!this.lightboxImage || !this.lightboxStage) return;

      const stageWidth = this.lightboxStage.clientWidth || 0;
      const stageHeight = this.lightboxStage.clientHeight || 0;
      const imageWidth = this.lightboxImage.clientWidth || 0;
      const imageHeight = this.lightboxImage.clientHeight || 0;
      const overflowX = Math.max(0, (imageWidth * zoom - stageWidth) / 2);
      const overflowY = Math.max(0, (imageHeight * zoom - stageHeight) / 2);

      this.lightboxZoom = zoom;
      this.lightboxPanX = Math.min(Math.max(panX, -overflowX), overflowX);
      this.lightboxPanY = Math.min(Math.max(panY, -overflowY), overflowY);
      this.lightboxImage.style.transform = `translate3d(${this.lightboxPanX}px, ${this.lightboxPanY}px, 0) scale(${this.lightboxZoom})`;
      this.lightboxImage.style.cursor = this.lightboxZoom > 1 ? 'grab' : 'zoom-in';
      this.lightboxImage.style.touchAction = this.lightboxZoom > 1 ? 'none' : 'pan-y';
    }
  }

  const descriptors = Object.getOwnPropertyDescriptors(ProductHeroGalleryLightboxGesturesBehavior.prototype);
  delete descriptors.constructor;

  const install = (GalleryClass) => {
    if (typeof GalleryClass !== 'function') {
      throw new TypeError('Product Hero Gallery Lightbox gestures require a class constructor.');
    }

    Object.defineProperties(GalleryClass.prototype, descriptors);
  };

  Object.defineProperty(window, MODULE_KEY, {
    configurable: true,
    enumerable: false,
    value: Object.freeze({ install }),
    writable: false,
  });
})();
